/**
 * Client for the Sparkforge Prototyper API.
 *
 * Authenticates with the signed-in user's Firebase ID token — the API verifies
 * it and takes the UID from the verified token, so the browser never asserts
 * who it is. This is what lets the Settings page mint Designer Keys for the
 * real account without any separate login.
 */
import { auth } from '@/firebase';

export const SPARKFORGE_API_URL = (
  process.env.VUE_APP_SPARKFORGE_API_URL || 'http://127.0.0.1:8900'
).replace(/\/$/, '');

/** Thrown with the API's own error envelope preserved. */
export class SparkforgeApiError extends Error {
  constructor(status, body) {
    const err = (body && body.error) || {};
    super(err.message || `Request failed (${status})`);
    this.name = 'SparkforgeApiError';
    this.status = status;
    this.code = err.code || 'http_error';
    this.field = err.field || null;
    this.body = body;
  }
}

/**
 * @param {boolean} allowAnonymous
 *   Send the call with no Authorization header when nobody is signed in,
 *   instead of failing here. Only for endpoints the API serves publicly —
 *   board and tile reads — so that a shared board link opens for a playtester
 *   who has no account. Refusing client-side made those endpoints unreachable
 *   from the very page built to show them.
 */
function shareQuery(share) {
  return share ? `?share=${encodeURIComponent(share)}` : '';
}

async function request(method, path, { body, signal, allowAnonymous = false } = {}) {
  const user = auth.currentUser;
  if (!user && !allowAnonymous) {
    throw new SparkforgeApiError(401, {
      error: { code: 'unauthenticated', message: 'You must be signed in.' }
    });
  }

  const headers = { 'Content-Type': 'application/json' };
  if (user) {
    // Firebase refreshes this automatically when it is close to expiry.
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }

  let response;
  try {
    response = await fetch(`${SPARKFORGE_API_URL}${path}`, {
      method,
      signal,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body)
    });
  } catch (networkError) {
    throw new SparkforgeApiError(0, {
      error: {
        code: 'unreachable',
        message:
          `Could not reach the Sparkforge API at ${SPARKFORGE_API_URL}. ` +
          'Start it with backend/sparkforge/scripts/run_api.sh, or set ' +
          'VUE_APP_SPARKFORGE_API_URL to point at a deployed instance.'
      }
    });
  }

  if (response.status === 204) return null;

  let payload = null;
  try {
    payload = await response.json();
  } catch (e) {
    payload = null;
  }

  if (!response.ok) throw new SparkforgeApiError(response.status, payload);
  return payload;
}

export const sparkforgeApi = {
  me: () => request('GET', '/v1/me'),
  scopes: () => request('GET', '/v1/scopes'),
  listKeys: () => request('GET', '/v1/keys'),
  createKey: ({ name, scopes, expiresDays }) =>
    request('POST', '/v1/keys', { body: { name, scopes, expiresDays } }),
  revokeKey: (keyId) => request('DELETE', `/v1/keys/${keyId}`),
  listGames: (scope = 'owned') => request('GET', `/v1/games?scope=${scope}`),
  generateImage: ({ prompt, style = 'card', aspectRatio = '1:1', gameId = null }) =>
    request('POST', '/v1/images/generate', {
      body: { prompt, style, aspectRatio, count: 1, persist: true, gameId }
    }),

  /**
   * Email a pending editor invitation to its recipient.
   *
   * The recipient is read server-side from the stored invite, so this cannot be
   * pointed at an arbitrary address. Returns { sent, provider, reason? };
   * `sent: false` is a normal answer when sending is switched off in this
   * environment, not a failure.
   */
  notifyEditorInvite: (gameId, editorId) =>
    request('POST', '/v1/notifications/editor-invite', { body: { gameId, editorId } }),

  // --- sharing -----------------------------------------------------------
  //
  // Access is owner-only and lives on its own endpoints rather than on the
  // game patch: an editor may rewrite a game but must not be able to hand it
  // to other people. All five return the same shape — visibility, shareUrl,
  // collaborators — so the dialog can re-render from any reply.

  /** Who can open this game, and who has been given access. Owner only. */
  getAccess: (gameId) => request('GET', `/v1/games/${gameId}/access`),

  /** 'private' | 'playtest' | 'public'. Mints a share link for 'playtest'. */
  setVisibility: (gameId, visibility) =>
    request('PUT', `/v1/games/${gameId}/visibility`, { body: { visibility } }),

  /** Add someone, or change their role. Same call for both. */
  shareWith: (gameId, email, role) =>
    request('PUT', `/v1/games/${gameId}/collaborators`, { body: { email, role } }),

  removeAccess: (gameId, email) =>
    request('DELETE', `/v1/games/${gameId}/collaborators/${encodeURIComponent(email)}`),

  /** New link, old one dead. The only way to cut off someone who has it. */
  rotateShareLink: (gameId) =>
    request('POST', `/v1/games/${gameId}/share-link`, { body: {} }),

  // --- card design -------------------------------------------------------
  listCardTemplates: () => request('GET', '/v1/card-templates'),
  getCardStyle: (gameId) => request('GET', `/v1/games/${gameId}/card-style`),
  setCardStyle: (gameId, style) =>
    request('PUT', `/v1/games/${gameId}/card-style`, { body: style }),
  setCardTemplate: (gameId, spec) =>
    request('PUT', `/v1/games/${gameId}/card-template`, { body: spec }),

  /**
   * Render a card WITHOUT saving it. Returns { svg, fit, template }.
   * `fit` reports lines, font size and whether the text had to shrink — the
   * same signal the print pipeline uses, so the warning shown while typing is
   * the truth about the printed card rather than a guess.
   */
  previewCard: (gameId, card, { builtin = null, style = null, signal } = {}) =>
    request('POST', `/v1/games/${gameId}/card-preview`, {
      signal,
      body: {
        name: card.name || '',
        description: card.description || '',
        type: card.type || null,
        subtype: card.subtype || null,
        flavorText: card.flavorText || null,
        repeats: card.repeats || 1,
        image_url: card.image_url || null,
        ...(builtin ? { builtin } : {}),
        ...(style ? { style } : {})
      }
    }),

  // --- player aids and releases ------------------------------------------
  listCards: (gameId) => request('GET', `/v1/games/${gameId}/cards`),
  listAids: (gameId) =>
    request('GET', `/v1/games/${gameId}/aids`, { allowAnonymous: true }),
  createAids: (gameId, aids) =>
    request('POST', `/v1/games/${gameId}/aids`, { body: { aids } }),
  updateAid: (aidId, patch) => request('PATCH', `/v1/aids/${aidId}`, { body: patch }),
  deleteAid: (aidId) => request('DELETE', `/v1/aids/${aidId}`),
  getAid: (aidId) => request('GET', `/v1/aids/${aidId}`, { allowAnonymous: true }),
  /** One aid as the sheet that prints: `svg` for written and card aids,
   *  `fileUrl` for uploads. Public, like the aid. */
  previewAid: (aidId) =>
    request('GET', `/v1/aids/${aidId}/preview`, { allowAnonymous: true }),

  listReleases: (gameId) => request('GET', `/v1/games/${gameId}/releases`),
  cutRelease: (gameId, { label, notes = null, publish = false }) =>
    request('POST', `/v1/games/${gameId}/releases`, {
      body: { label, publish, ...(notes ? { notes } : {}) }
    }),
  updateRelease: (releaseId, patch) =>
    request('PATCH', `/v1/releases/${releaseId}`, { body: patch }),
  deleteRelease: (releaseId) => request('DELETE', `/v1/releases/${releaseId}`),

  /**
   * Export a PDF. `what` is cards | rules | board | aids.
   * Returns { url, ... } — the API stores the file and hands back a link
   * rather than a multi-megabyte base64 blob.
   */
  exportPdf: (gameId, what, body = {}) =>
    request('POST', `/v1/games/${gameId}/exports/${what}`, {
      body: { return_type: 'url', ...body }
    }),

  /** What has moved since the last release — the "is what I handed out still
   *  what I have?" question that no single part of the game can answer. */
  unreleasedChanges: (gameId) => request('GET', `/v1/games/${gameId}/changes`),

  // --- boards and tiles --------------------------------------------------
  listBoardTemplates: () => request('GET', '/v1/board-templates'),
  /** A board's stored print PDFs: { color, bw, ready }, each with `url` and
   *  whether it still matches the board (`fresh`). */
  getBoardPrints: (boardId) =>
    request('GET', `/v1/boards/${boardId}/prints`, { allowAnonymous: true }),
  /** Make whichever prints are missing or out of date. Quick when current. */
  makeBoardPrints: (boardId) =>
    request('POST', `/v1/boards/${boardId}/prints`, { body: {} }),
  /** The board judge: ten quality checks, each with evidence and a fix. */
  judgeBoard: (boardId, stage = 'playtest') =>
    request('GET', `/v1/boards/${boardId}/judge?stage=${encodeURIComponent(stage)}`),
  /** A deck's kept colour and black-and-white PDFs. Readable wherever the game is. */
  getCardPrints: (gameId, share = null) =>
    request('GET', `/v1/games/${gameId}/card-prints${shareQuery(share)}`, { allowAnonymous: true }),
  /** Make whichever deck prints are missing or out of date. Quick when current. */
  makeCardPrints: (gameId) =>
    request('POST', `/v1/games/${gameId}/card-prints`, { body: {} }),
  /** The deck kept with each cut version, newest first — what players download. */
  listDecks: (gameId, share = null) =>
    request('GET', `/v1/games/${gameId}/decks${shareQuery(share)}`, { allowAnonymous: true }),
  createBoard: (gameId, board) =>
    request('POST', `/v1/games/${gameId}/boards`, { body: board }),
  listBoards: (gameId) =>
    request('GET', `/v1/games/${gameId}/boards`, { allowAnonymous: true }),
  getBoard: (boardId) =>
    request('GET', `/v1/boards/${boardId}`, { allowAnonymous: true }),
  updateBoard: (boardId, patch) =>
    request('PATCH', `/v1/boards/${boardId}`, { body: patch }),

  listTiles: (boardId) =>
    request('GET', `/v1/boards/${boardId}/tiles`, { allowAnonymous: true }),
  getTile: (tileId) => request('GET', `/v1/tiles/${tileId}`, { allowAnonymous: true }),
  createTiles: (boardId, tiles) =>
    request('POST', `/v1/boards/${boardId}/tiles`, { body: { tiles } }),
  updateTile: (tileId, patch) =>
    request('PATCH', `/v1/tiles/${tileId}`, { body: patch }),
  deleteTile: (tileId) => request('DELETE', `/v1/tiles/${tileId}`),
  reorderTiles: (boardId, tileIds) =>
    request('POST', `/v1/boards/${boardId}/tiles/reorder`, { body: { tileIds } }),

  /**
   * Render a board WITHOUT saving. Returns { svg, fit, template, tiles }.
   *
   * The same call an agent makes, so the picture in the app and the picture
   * the agent checked its work against are the same bytes — and `fit` is the
   * truth about the printed board rather than a guess.
   */
  previewBoard: (boardId, { template = null, style = null, mode = 'play', signal } = {}) =>
    request('POST', `/v1/boards/${boardId}/preview`, {
      signal,
      allowAnonymous: true,
      body: {
        mode,
        ...(template ? { template } : {}),
        ...(style ? { style } : {})
      }
    })
};

/**
 * Wrap an SVG string as a data URI for an <img>.
 *
 * Deliberately NOT v-html: an <img> cannot execute script, so even though this
 * SVG comes from our own API (which escapes every card field server-side),
 * there is no injection surface at all.
 */
export function svgToDataUri(svg) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * The MCP client config block, with the freshly-minted token filled in.
 *
 * Kept here rather than in the component so the API URL and env var names have
 * exactly one definition.
 */
export function mcpConfigSnippet(token, { pythonPath, cwd } = {}) {
  const python =
    pythonPath || '/ABSOLUTE/PATH/TO/board-prototyper/backend/sparkforge/.venv/bin/python';
  const dataDir = cwd || '/ABSOLUTE/PATH/TO/board-prototyper/backend/sparkforge/data';
  return JSON.stringify(
    {
      mcpServers: {
        sparkforge: {
          command: python,
          args: ['-m', 'sparkforge.mcp.server'],
          env: {
            SPARKFORGE_STORE: 'firestore',
            SPARKFORGE_API_KEY: token,
            SPARKFORGE_DATA_DIR: dataDir,
            SPARKFORGE_PUBLIC_URL: SPARKFORGE_API_URL,
            FIREBASE_PROJECT_ID: 'boardbuilder-f6627'
          }
        }
      }
    },
    null,
    2
  );
}
